import { createSignal, createMemo, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { parseNumericInput } from '../lib/money.js';

// RincianHarian — the unified per-run derivation surface (15-derivation-contract).
// For a proc scheme it is the ONE place attendance is reviewed and edited: the
// proc's payable period totals (read-only) sit on each employee row, and the
// per-day drill-down shows the proc's evidence (Status / clock-in / out, read-only)
// plus its editable inputs (Hadir checkbox, Keterangan, …). Editing a day writes
// pay_day_attr, the proc re-derives, and the totals update reactively. "Terapkan"
// materialises the totals into the per-period inputs the DSL prices. The grid is a
// DUMB renderer: every column, flag, widget and badge tone comes from the proc's
// RS1 spec. It also still serves non-proc schemes that declare pay_scheme_day_column
// columns (e.g. TKBM Mode/Kendaraan), and shows live scan/SPL evidence for any scheme.

const DAY_NAMES = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
function dayName(date) {
  const d = new Date(date + 'T00:00:00');
  return isNaN(d) ? '' : DAY_NAMES[d.getDay()];
}

async function getJSON(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || ('HTTP ' + r.status));
  return r.json();
}
async function postJSON(url, body) {
  const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: r.ok, data: await r.json().catch(() => ({})) };
}

function daysBetween(start, end) {
  const out = [];
  if (!start || !end) return out;
  const d = new Date(start + 'T00:00:00'), e = new Date(end + 'T00:00:00');
  while (d <= e) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const EMPTY_EV = { scans: [], spls: [] };

// parse a contract badge style ("Hadir=success;Alpha=danger") → { value: tone }.
function toneMap(style) {
  const m = {};
  for (const part of (style || '').split(';')) {
    const [k, v] = part.split('=');
    if (k && v) m[k.trim()] = v.trim();
  }
  return m;
}

// trim trailing zeros from a proc decimal for display ("4.7500" → "4.75", "3.0000"
// → "3"). Non-numeric values pass through unchanged.
function fmtNum(v) {
  if (v === '' || v == null) return '0';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

export default function RincianHarian(props) {
  // props: runId (number), employees ([{nik,name}]), onApplied (() => void)
  const [data, setData] = createStore({ loaded: false, columns: [], values: {}, meta: {}, evidence: {}, procColumns: [], procCells: {}, procPeriod: {}, editable: false, periodStart: '', periodEnd: '' });
  const [open, setOpen] = createStore({}); // nik -> bool
  const [msg, setMsg] = createSignal(null);
  const [openSpl, setOpenSpl] = createSignal(''); // "nik|date|idx"
  const [applying, setApplying] = createSignal(false);
  const [deriving, setDeriving] = createSignal(false); // a debounced re-derive is pending/running
  // One-shot guard: after a successful Terapkan the button stays disabled so a
  // second (accidental) click cannot re-overwrite manual per-period edits with
  // the derived totals. The armed/applied state is stored ON THE RUN
  // (pay_run.attendance_applied_at, migration 047) so it holds across tabs and
  // browsers — the server refuses a re-apply until unlocked. It re-arms on
  // Muat ulang (explicit unlock) or on any day edit (the server clears the
  // flag when the totals change, since re-applying is then meaningful again).
  const [applied, setApplied] = createSignal(false);
  // ── display filters (view-only — they NEVER affect Terapkan, which always
  // materialises the whole run, nor the derivation; they just narrow the rows shown).
  const [search, setSearch] = createSignal('');
  const [deptFilter, setDeptFilter] = createSignal('');
  const [onlyReview, setOnlyReview] = createSignal(false);

  async function load() {
    try {
      const d = await getJSON(`/api/run/${props.runId}/day-attrs`);
      // reconcile (not a fresh object) so a RELOAD mutates leaf cells in place —
      // a Hadir toggle re-renders the day cell AND the period total on the parent
      // row without remounting (verified: a createEffect on these paths re-fires).
      setData(reconcile({
        loaded: true, columns: d.columns ?? [], values: d.values ?? {}, meta: d.meta ?? {},
        evidence: d.evidence ?? {}, procColumns: d.procColumns ?? [], procCells: d.procCells ?? {},
        procPeriod: d.procPeriod ?? {}, editable: !!d.editable, periodStart: d.periodStart, periodEnd: d.periodEnd,
      }));
      setApplied(!!d.appliedAt); // server truth — consistent across tabs
    } catch (e) { setMsg({ ok: false, msg: e.message }); }
  }
  load();

  // Muat ulang: explicitly re-arm the server-side guard, then reload — the ONE
  // deliberate way to enable a re-apply without editing any day data.
  async function muatUlang() {
    setDeriving(false);
    await postJSON(`/api/run/${props.runId}/attendance/unlock`, {});
    load();
  }

  // ── optimistic + debounced re-derive ──────────────────────────────────────
  // The proc re-derives the WHOLE run (~2s), so we never run it per keystroke:
  // each edit is reflected locally at once (instant widget) and persisted right
  // away, but the expensive re-derive is COALESCED into one call after the user
  // pauses (400ms) — so rapid toggling no longer queues N full derivations.
  let reloadTimer = null;
  function scheduleReload() {
    setDeriving(true);
    setApplied(false); // a day edit changes the totals — Terapkan is meaningful again
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(async () => { reloadTimer = null; await load(); setDeriving(false); }, 400);
  }

  const days = () => daysBetween(data.periodStart, data.periodEnd);
  const meta = (nik) => data.meta[nik] ?? { fid: '', department: '', position: '' };
  const evid = (nik, date) => {
    const e = data.evidence[nik]?.[date];
    return e ? { scans: e.scans || [], spls: e.spls || [] } : EMPTY_EV;
  };
  function toggleSpl(nik, date, i) {
    const k = `${nik}|${date}|${i}`;
    setOpenSpl((cur) => (cur === k ? '' : k));
  }

  // contract column groups (the renderer orders by these, not by hardcoded names).
  const periodCols = createMemo(() => (data.procColumns || []).filter((c) => c.grain === 'period'));
  const evidenceCols = createMemo(() => (data.procColumns || []).filter((c) => c.grain === 'day' && !c.editable));
  const inputCols = createMemo(() => (data.procColumns || []).filter((c) => c.grain === 'day' && c.editable));
  // proc editable enum/select day columns also get a parent-row "set semua" (bulk
  // set all applicable days), mirroring the old pay_scheme_day_column convenience.
  const inputEnumCols = createMemo(() => inputCols().filter((c) => c.dataType === 'enum' || c.render === 'select'));
  const payableCols = createMemo(() => periodCols().filter((c) => c.payable));
  const isProc = createMemo(() => (data.procColumns || []).length > 0);
  // distinct departments among the run's employees (the filter dropdown options).
  const departments = createMemo(() => {
    const set = new Set();
    for (const e of props.employees ?? []) { const d = meta(e.nik).department; if (d) set.add(d); }
    return [...set].sort();
  });
  // badge evidence columns whose tone map flags a reviewable state. The proc owns
  // which value → which tone (e.g. Status "Alpha=danger;BelumScan=warning"); the
  // renderer just treats warning/danger as "perlu ditinjau".
  const reviewBadgeCols = createMemo(() => evidenceCols().filter((c) => c.render === 'badge'));

  const pcell = (nik, date, code) => data.procCells[nik]?.[date]?.[code] ?? { value: '', applicable: true };
  const ptotal = (nik, code) => data.procPeriod[nik]?.[code] ?? '';
  const stored = (nik, date, name) => data.values[nik]?.[date]?.[name] ?? '';

  // perlu ditinjau: no fingerprint (attendance can't be derived from scans) OR any
  // day whose proc badge-evidence resolves to a warning/danger tone — that covers
  // Alpha, BelumScan (lupa clock-in / clock-out), etc., per the proc's own style map.
  function needsReview(nik) {
    if (!meta(nik).fid) return true;
    for (const c of reviewBadgeCols()) {
      const tones = toneMap(c.style);
      for (const date of days()) {
        const cell = data.procCells[nik]?.[date]?.[c.code];
        if (!cell || !cell.applicable || !cell.value) continue;
        const t = tones[cell.value];
        if (t === 'danger' || t === 'warning') return true;
      }
    }
    return false;
  }
  // the employees actually rendered: department + NIK/name search + review filter.
  const visibleEmployees = createMemo(() => {
    const q = search().trim().toLowerCase(), dept = deptFilter(), review = onlyReview();
    return (props.employees ?? []).filter((e) => {
      if (dept && meta(e.nik).department !== dept) return false;
      if (q && !`${e.nik} ${e.name || ''}`.toLowerCase().includes(q)) return false;
      if (review && !needsReview(e.nik)) return false;
      return true;
    });
  });

  // write a proc-declared editable cell (Hadir/Keterangan) → pay_day_attr. The
  // widget updates instantly (optimistic), the value persists immediately, and the
  // proc re-derive (Status + period totals) is debounced via scheduleReload().
  async function saveProc(nik, date, code, value) {
    if (data.procCells[nik]?.[date]?.[code]) setData('procCells', nik, date, code, 'value', value); // optimistic
    const r = await postJSON(`/api/run/${props.runId}/day-attrs`, { cells: [{ nik, date, name: code, value }] });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); load(); return; } // resync on error
    scheduleReload();
  }
  // scheme day-column write (pay_scheme_day_column, e.g. TKBM Mode/Kendaraan).
  async function saveCell(nik, date, col, value) {
    putLocalValue(nik, date, col.name, value); // optimistic
    const r = await postJSON(`/api/run/${props.runId}/day-attrs`, { cells: [{ nik, date, name: col.name, value }] });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); load(); return; }
    scheduleReload();
  }
  async function setAllScheme(nik, col, value) {
    const cells = days().map((date) => ({ nik, date, name: col.name, value }));
    for (const date of days()) putLocalValue(nik, date, col.name, value); // optimistic
    const r = await postJSON(`/api/run/${props.runId}/day-attrs`, { cells });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); load(); return; }
    scheduleReload();
    setMsg({ ok: true, msg: `${col.label}: semua hari di-set "${value || '(default)'}" untuk ${nik}.` });
  }
  // bulk-set a proc editable enum column (e.g. TKBM Mode) for every APPLICABLE day
  // of one employee — skips days the proc marks inapplicable (e.g. Kendaraan on a
  // non-Kirim day), so no junk override is written where the proc ignores it.
  async function setAllProc(nik, c, value) {
    const dates = days().filter((date) => pcell(nik, date, c.code).applicable);
    if (!dates.length) { setMsg({ ok: false, msg: `${c.label}: tidak ada hari yang berlaku untuk ${nik}.` }); return; }
    for (const date of dates) if (data.procCells[nik]?.[date]?.[c.code]) setData('procCells', nik, date, c.code, 'value', value); // optimistic
    const r = await postJSON(`/api/run/${props.runId}/day-attrs`, { cells: dates.map((date) => ({ nik, date, name: c.code, value })) });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); load(); return; }
    scheduleReload();
    setMsg({ ok: true, msg: `${c.label}: semua hari berlaku di-set "${value || '(default)'}" untuk ${nik}.` });
  }
  // optimistic write into data.values (scheme day-columns); '' deletes the cell.
  function putLocalValue(nik, date, name, value) {
    setData('values', nik, (v) => {
      const day = { ...((v || {})[date] || {}) };
      if (value === '') delete day[name]; else day[name] = value;
      return { ...(v || {}), [date]: day };
    });
  }

  // Terapkan: materialise the proc's payable totals into the per-period inputs.
  async function terapkan() {
    setApplying(true); setMsg(null);
    const cells = [];
    for (const emp of props.employees) {
      for (const c of payableCols()) {
        cells.push({ nik: emp.nik, code: c.code, value: String(ptotal(emp.nik, c.code) ?? '') });
      }
    }
    let r = await postJSON(`/api/run/${props.runId}/attendance/apply`, { cells });
    // Another tab already applied (server-side guard): offer a deliberate
    // unlock + retry instead of a dead-end error.
    if (!r.ok && r.data.alreadyApplied
        && confirm('Total sudah pernah diterapkan (mungkin dari tab lain). Terapkan lagi dan timpa nilai di input per periode?')) {
      await postJSON(`/api/run/${props.runId}/attendance/unlock`, {});
      r = await postJSON(`/api/run/${props.runId}/attendance/apply`, { cells });
    }
    setApplying(false);
    if (r.ok) {
      setApplied(true);
      setMsg({ ok: true, msg: `${r.data.written} nilai dikirim ke input per periode.` });
      props.onApplied?.();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  // colspan for the drill row = the PARENT table's column count, so the inner
  // day-table spans the full width: Karyawan + period totals + scheme day-cols
  // + proc enum "set semua" + the expand cell.
  const drillSpan = () => 1 + periodCols().length + data.columns.length + inputEnumCols().length + 1;

  // ── widgets ──────────────────────────────────────────────────────────────
  // `cell` is a THUNK read inside the returned JSX so each access is tracked and
  // re-renders on re-derive (a captured value would freeze the cell — Solid only
  // tracks reads that happen inside a reactive expression).
  function evidenceCell(nik, date, c) {
    const cell = () => pcell(nik, date, c.code);
    return (
      <Show when={cell().applicable && cell().value} fallback={<span class="vg-dim">—</span>}>
        <Show when={c.render === 'badge'} fallback={<span>{c.render === 'number' ? fmtNum(cell().value) : cell().value}</span>}>
          <span class={`badge ${toneMap(c.style)[cell().value] || 'info'}`}>{cell().value}</span>
        </Show>
      </Show>
    );
  }
  function inputCell(nik, date, c) {
    const cell = () => pcell(nik, date, c.code);
    return (
      <Show when={cell().applicable} fallback={<span class="vg-dim">—</span>}>
        <Show when={c.dataType === 'bool' || c.render === 'checkbox'} fallback={
          <Show when={c.dataType === 'enum' || c.render === 'select'} fallback={
            <Show when={data.editable} fallback={<span>{cell().value || <span class="vg-dim">—</span>}</span>}>
              <input type="text" class="pg-cell" value={cell().value}
                onChange={(e) => saveProc(nik, date, c.code, c.dataType === 'angka' ? parseNumericInput(e.currentTarget.value) : e.currentTarget.value)} />
            </Show>
          }>
            <select class="kg-select" disabled={!data.editable} value={cell().value}
              onChange={(e) => saveProc(nik, date, c.code, e.currentTarget.value)}>
              <option value="">(default)</option>
              <For each={c.choices}>{(ch) => <option value={ch}>{ch}</option>}</For>
            </select>
          </Show>
        }>
          <input type="checkbox" disabled={!data.editable}
            checked={cell().value === '1' || cell().value === 'true'}
            onChange={(e) => saveProc(nik, date, c.code, e.currentTarget.checked ? '1' : '0')} />
        </Show>
      </Show>
    );
  }

  return (
    <Show when={data.loaded && (props.employees?.length)}>
      <div class="card card-pad ab-rekap">
        <div class="ky-vars-head">
          <h4 class="kg-hist-title">Rincian Harian</h4>
          <span class="kg-meta-text" style="margin-left:8px">kehadiran &amp; atribut operasional per hari</span>
        </div>
        <p class="ky-vars-note">
          <Show when={isProc()} fallback="Buka per hari untuk melihat jam scan dan SPL tiap hari.">
            Total per periode di setiap baris <b>dihitung dari kehadiran harian</b> — buka
            <b> per hari</b>, lalu centang/hapus <b>Hadir</b> untuk mengubahnya. Klik
            <b> Terapkan ke input per periode</b> untuk mengunci total ke input yang dihitung gaji.
          </Show>
          {data.editable ? '' : ' (run terkunci — hanya lihat).'}
        </p>
        <Show when={msg()}><div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin:8px 0">{msg().msg}</div></Show>

        {/* ── view filters (NIK/name search · department · perlu-ditinjau) ────── */}
        <div class="lb-filter">
          <label class="kg-field rh-search">
            <span>Cari karyawan</span>
            <input type="text" placeholder="NIK atau nama…" value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)} />
          </label>
          <Show when={departments().length}>
            <label class="kg-field rh-dept">
              <span>Departemen</span>
              <select class="kg-select" value={deptFilter()} onChange={(e) => setDeptFilter(e.currentTarget.value)}>
                <option value="">Semua departemen</option>
                <For each={departments()}>{(d) => <option value={d}>{d}</option>}</For>
              </select>
            </label>
          </Show>
          <label class="ky-check rh-review" title="alpha, tanpa FID, lupa clock-in/clock-out, dll.">
            <input type="checkbox" checked={onlyReview()} onInput={(e) => setOnlyReview(e.currentTarget.checked)} />
            hanya perlu ditinjau
          </label>
          <div class="spacer" />
          <span class="kg-meta-text rh-count">{visibleEmployees().length} / {props.employees.length} karyawan</span>
        </div>

        <div class="pg-grid-wrap">
          <table class="data pg-grid">
            <thead>
              <tr>
                <th class="pg-sticky">Karyawan</th>
                <For each={periodCols()}>{(c) => <th title={c.code} style="text-align:center">{c.label}</th>}</For>
                <For each={data.columns}>{(c) => <th title={c.name}>{c.label} <span class="kg-meta-text">(set semua)</span></th>}</For>
                <For each={inputEnumCols()}>{(c) => <th title={c.code}>{c.label} <span class="kg-meta-text">(set semua)</span></th>}</For>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For each={visibleEmployees()} fallback={
                <tr><td colspan={drillSpan()} class="kg-meta-text" style="padding:16px; text-align:center">Tidak ada karyawan yang cocok dengan filter.</td></tr>
              }>{(emp) =>
                <>
                  <tr classList={{ 'ab-row-sticky': open[emp.nik] }}>
                    <td class="pg-sticky">
                      <div class="kg-code">{emp.nik}</div>
                      <div class="kg-sub">{emp.name}</div>
                      <div class="kg-meta-text">
                        FID {meta(emp.nik).fid || '—'} · {meta(emp.nik).department || '—'} · {meta(emp.nik).position || '—'}
                      </div>
                    </td>
                    <For each={periodCols()}>{(c) =>
                      <td style="text-align:center; font-variant-numeric:tabular-nums">{fmtNum(ptotal(emp.nik, c.code))}</td>
                    }</For>
                    <For each={data.columns}>{(col) =>
                      <td>
                        <Show when={data.editable && col.valueType === 'enum'} fallback={<span class="vg-dim">per hari</span>}>
                          <select class="kg-select" onChange={(e) => { setAllScheme(emp.nik, col, e.currentTarget.value); e.currentTarget.selectedIndex = 0; }}>
                            <option value="">— set semua —</option>
                            <For each={col.choices}>{(ch) => <option value={ch}>{ch}</option>}</For>
                          </select>
                        </Show>
                      </td>
                    }</For>
                    <For each={inputEnumCols()}>{(c) =>
                      <td>
                        <Show when={data.editable} fallback={<span class="vg-dim">per hari</span>}>
                          <select class="kg-select" onChange={(e) => { setAllProc(emp.nik, c, e.currentTarget.value); e.currentTarget.selectedIndex = 0; }}>
                            <option value="">— set semua —</option>
                            <For each={c.choices}>{(ch) => <option value={ch}>{ch}</option>}</For>
                          </select>
                        </Show>
                      </td>
                    }</For>
                    <td><button class="btn ghost" onClick={() => setOpen(emp.nik, (o) => !o)}>{open[emp.nik] ? 'tutup' : 'per hari'}</button></td>
                  </tr>
                  <Show when={open[emp.nik]}>
                    <tr class="ab-drill-row">
                      <td colspan={drillSpan()}>
                        <table class="data ab-days">
                          <thead><tr>
                            <th>Tanggal</th>
                            <For each={evidenceCols()}>{(c) => <th>{c.label}</th>}</For>
                            <th>SPL</th>
                            <For each={data.columns}>{(c) => <th>{c.label}</th>}</For>
                            <For each={inputCols()}>{(c) => <th>{c.label}</th>}</For>
                            <th>Data Raw</th>
                          </tr></thead>
                          <tbody>
                            <For each={days()}>{(date) =>
                              <tr>
                                <td class="ky-nik">{date}<div class="kg-meta-text">{dayName(date)}</div></td>
                                <For each={evidenceCols()}>{(c) => <td>{evidenceCell(emp.nik, date, c)}</td>}</For>
                                <td class="rh-spl">
                                  <Show when={evid(emp.nik, date).spls.length} fallback={<span class="vg-dim">—</span>}>
                                    <For each={evid(emp.nik, date).spls}>{(spl, i) =>
                                      <span style="position:relative;display:inline-block;margin-right:6px">
                                        <a style="text-decoration:underline;cursor:pointer" onClick={() => toggleSpl(emp.nik, date, i())} title={`${spl.start} – ${spl.end}`}>{spl.no}</a>
                                        <Show when={openSpl() === `${emp.nik}|${date}|${i()}`}>
                                          <span style="position:absolute;left:0;top:100%;margin-top:2px;z-index:5;white-space:nowrap;font-size:12px;padding:4px 8px;border-radius:6px;background:var(--bg-solid-2);border:1px solid var(--border);color:var(--fg);box-shadow:var(--shadow-2)">
                                            {spl.start} – {spl.end}
                                          </span>
                                        </Show>
                                      </span>
                                    }</For>
                                  </Show>
                                </td>
                                <For each={data.columns}>{(col) =>
                                  <td>
                                    <Show when={data.editable} fallback={<span>{stored(emp.nik, date, col.name) || <span class="vg-dim">{col.defaultVal || '—'}</span>}</span>}>
                                      <Show when={col.valueType === 'enum'} fallback={
                                        <input type="text" class="pg-cell" value={stored(emp.nik, date, col.name)} placeholder={col.defaultVal}
                                          onChange={(e) => saveCell(emp.nik, date, col, col.valueType === 'angka' ? parseNumericInput(e.currentTarget.value) : e.currentTarget.value)} />
                                      }>
                                        <select class="kg-select" value={stored(emp.nik, date, col.name)}
                                          onChange={(e) => saveCell(emp.nik, date, col, e.currentTarget.value)}>
                                          <option value="">{col.defaultVal ? `(default: ${col.defaultVal})` : '(default)'}</option>
                                          <For each={col.choices}>{(ch) => <option value={ch}>{ch}</option>}</For>
                                        </select>
                                      </Show>
                                    </Show>
                                  </td>
                                }</For>
                                <For each={inputCols()}>{(c) => <td>{inputCell(emp.nik, date, c)}</td>}</For>
                                <td class="rh-jam">
                                  <Show when={evid(emp.nik, date).scans.length} fallback={<span class="vg-dim">—</span>}>
                                    <For each={evid(emp.nik, date).scans}>{(s) =>
                                      <div>{s.time} <span class="kg-meta-text">{s.type === 'Masuk' ? 'masuk' : s.type === 'Pulang' ? 'pulang' : ''}</span></div>
                                    }</For>
                                  </Show>
                                </td>
                              </tr>
                            }</For>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </Show>
                </>
              }</For>
            </tbody>
          </table>
        </div>

        <Show when={isProc() && data.editable}>
          <div style="display:flex; gap:8px; align-items:center; margin-top:12px">
            <button class="btn primary" disabled={applying() || deriving() || applied()} onClick={terapkan}
              title={applied() ? 'Sudah diterapkan — klik Muat ulang untuk mengaktifkan kembali' : undefined}>
              {applying() ? 'Mengirim…' : applied() ? 'Sudah diterapkan ✓' : 'Terapkan ke input per periode'}
            </button>
            <button class="btn ghost" onClick={muatUlang}>Muat ulang</button>
            <Show when={deriving()} fallback={
              <Show when={applied()} fallback={
                <span class="kg-meta-text">Total dihitung dari kehadiran harian; ubah manual setelah diterapkan di input per periode.</span>
              }>
                <span class="kg-meta-text">Sudah diterapkan — edit manual di input per periode aman dari klik ganda. Muat ulang untuk menerapkan lagi.</span>
              </Show>
            }>
              <span class="kg-meta-text" style="opacity:0.85">⏳ menghitung ulang total…</span>
            </Show>
          </div>
        </Show>
      </div>
    </Show>
  );
}
